import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Select, Checkbox, Tooltip as AntTooltip, Row, Col } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Option } = Select;

const Speed = ({ streetName, data }) => {
  const options = ["1min", "5min", "10min", "15min", "30min"];
  const defaultSelectedOption = "10min";
  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);
  const [extractedFlowData, setExtractedFlowData] = useState([]);
  const [vehicleVisibility, setVehicleVisibility] = useState({});
  const [mediaSpSec, setMediaSpSec] = useState(null);
  const [ticks, setTicks] = useState({});

  const colors = useMemo(
    () => [
      "#8884d8",
      "#82ca9d",
      "#ffc658",
      "#ff7300",
      "#ff84f0",
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#af111c",
      "#c02e17",
    ],
    []
  );

  // Helper to normalize IDs (handles "455.0" vs 455 format)
  const normalizeId = (id) => {
    if (id === null || id === undefined) return null;
    const parsed = typeof id === 'string' ? parseFloat(id) : id;
    return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
  };

  useEffect(() => {
    if (streetName) {
      const normalizedStreetName = normalizeId(streetName);
      const streetData = data.find((street) => normalizeId(street.id) === normalizedStreetName);

      if (streetData && streetData.indicatoriGlobali) {
        setMediaSpSec(streetData.indicatoriGlobali.mediaSpSec);
      }
      if (
        streetData?.serieTemporale?.speed &&
        streetData.serieTemporale.inputCount[selectedOption]
      ) {
        const graphData = streetData.serieTemporale.speed[selectedOption];
        const newData = Object.keys(graphData).map((vehicleType, index) => ({
          name: vehicleType,
          data: Object.entries(graphData[vehicleType]).map(([key, value]) => ({
            x: parseInt(key),
            y: value,
          })),
          color: colors[index % colors.length],
        }));
        setExtractedFlowData(newData);

        const initialVisibility = newData.reduce((acc, v) => {
          acc[v.name] = true;
          return acc;
        }, {});
        setVehicleVisibility(initialVisibility);
      }

      setTicks({
        "1min": [10, 20, 30, 40, 50, 60],
        "5min": [10, 20, 30, 40, 50, 60],
        "10min": [10, 20, 30, 40, 50, 60],
        "15min": [15, 30, 45, 60],
        "30min": [30, 40, 50, 60],
      });
    }
  }, [streetName, selectedOption, data, colors]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setVehicleVisibility((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <div>
      {/* Title with tooltip */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <h2 style={{ marginRight: 8 }}>Velocità</h2>
        <AntTooltip title="Velocità media che un veicolo sperimenta, entrando nella sezione stradale in uno specifico intervallo di tempo. La durata degli intervalli può essere selezionata attraverso l'apposito menu a tendina.">
          <InfoCircleOutlined style={{ fontSize: 16, color: "#768079" }} />
        </AntTooltip>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              height={300}
              margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="x"
                type="number"
                domain={["dataMin", "dataMax"]}
                allowDataOverflow={true}
                ticks={ticks[selectedOption]}
                label={{ value: "Time", position: "insideBottom", offset: -10 }}
              />
              <YAxis
                label={{ value: "km/h", angle: -90, position: "insideLeft" }}
              />
              <Tooltip />
              {extractedFlowData.map((vehicle) =>
                vehicleVisibility[vehicle.name] ? (
                  <Line
                    key={vehicle.name}
                    type="monotone"
                    dataKey="y"
                    data={vehicle.data}
                    name={vehicle.name}
                    stroke={vehicle.color}
                  />
                ) : null
              )}
            </LineChart>
          </ResponsiveContainer>
        </Col>

        <Col
          xs={24}
          lg={8}
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          {extractedFlowData.map((vehicle) => (
            <Checkbox
              key={vehicle.name}
              checked={vehicleVisibility[vehicle.name]}
              onChange={handleCheckboxChange}
              name={vehicle.name}
              style={{ color: vehicle.color }}
            >
              {vehicle.name}
            </Checkbox>
          ))}

          <h3>{`Velocità media: ${mediaSpSec}`}</h3>
        </Col>
      </Row>

      <div style={{ marginTop: 20, display: "flex", justifyContent: "center" }}>
        <Select
          value={selectedOption}
          onChange={setSelectedOption}
          style={{ width: 130 }}
        >
          {options.map((opt) => (
            <Option key={opt} value={opt}>
              {opt}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

export default Speed;
